import { useState, useRef, useCallback, useEffect } from "react";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Textarea } from "@/shared/components/ui/textarea";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { Brain, Send, Loader2, AlertCircle, Volume2, Settings2, FileDown, ExternalLink, Presentation, Square, Trash2, FileText, X } from "lucide-react";
import { exportChatToPdfOfficial } from "@/shared/lib/exportChatToPdfOfficial";
import {
  usePersistedChat,
  clearPersistedChat,
  getStorageKey,
} from "@/shared/hooks/usePersistedChat";
import { useToast } from "@/shared/hooks/use-toast";
import AiMarkdownRenderer from "@/shared/components/finance/AiMarkdownRenderer";
import { getSupabaseFunctionUrl, getAccessTokenForEdge } from "@/shared/lib/supabaseFunctions";
import { supabase } from "@/shared/integrations/supabase/client";
import VoiceCommandButton from "@/shared/components/ui/VoiceCommandButton";
import { ChatGradientWrapper } from "@/shared/components/ai/ChatGradientWrapper";
import AiMessageActions from "@/shared/components/ai/AiMessageActions";
import { LimiteMensagemChat } from "./LimiteMensagemChat";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/components/ui/select";
import {
  getDocumentTypeLabel,
  getLegalCategoryLabel,
  getFolderLabel,
  DOCUMENT_TYPES,
  LEGAL_CATEGORIES,
  DOCUMENT_FOLDERS,
} from "@/shared/constants/institutionDocuments";
import ArchivingModalForm, { type SuggestArchivingMeta } from "./ArchivingModalForm";

export type { SuggestArchivingMeta };

type Msg = { role: "user" | "assistant"; content: string; id?: string };

interface Props {
  institutionId: string;
  institutionName: string;
  /** Nome exibido e usado nas mensagens (padrão: Assistente IA). */
  iaName?: string;
  /** Nível de acesso do usuário externo: A, B ou C. Null/omitido = interno (sem filtro de pasta na IA). */
  nivel?: "A" | "B" | "C" | null;
  /** Sufixo da sessão persistida — evita misturar o chat principal do portal com o chat do detalhe de processo. */
  chatSessionSuffix?: string | null;
  /** Texto anexado ao system prompt na Edge (ex.: resumo do processo em foco). */
  systemPromptExtra?: string | null;
  /** Layout mais baixo para embutir em painéis (ex.: sheet do portal). */
  compact?: boolean;
}

async function streamInstitutionChat({
  institutionId,
  messages,
  nivel,
  systemPromptExtra,
  onDelta,
  onDone,
  onError,
}: {
  institutionId: string;
  messages: Msg[];
  nivel?: "A" | "B" | "C" | null;
  systemPromptExtra?: string | null;
  onDelta: (text: string) => void;
  onDone: () => void;
  onError: (msg: string) => void;
}) {
  const url = getSupabaseFunctionUrl("institution-ai");
  if (!url) {
    onError("Assistente temporariamente indisponível.");
    return;
  }
  const token = await getAccessTokenForEdge();
  if (!token) {
    onError("Sessão expirada ou não autenticado. Faça login novamente.");
    return;
  }
  const resp = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      institution_id: institutionId,
      messages,
      nivel: nivel ?? null,
      system_prompt_extra: systemPromptExtra?.trim() || null,
    }),
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    const msg = (err as { error?: string })?.error;
    if (resp.status === 502) {
      onError(msg || "Serviço do assistente temporariamente indisponível (502). Aguarde alguns segundos e tente novamente.");
    } else if (resp.status === 503) {
      onError(msg || "Assistente temporariamente indisponível. Tente novamente em instantes.");
    } else if (resp.status === 504) {
      onError(msg || "A resposta demorou demais. Tente encurtar a pergunta ou enviar de novo.");
    } else if (resp.status === 403) {
      onError(msg || "Sem permissão para usar o assistente nesta instituição.");
    } else {
      onError(msg || `Erro ${resp.status}`);
    }
    return;
  }
  if (!resp.body) {
    onError("Sem resposta");
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let done = false;

  while (!done) {
    const { done: readerDone, value } = await reader.read();
    if (readerDone) break;
    buffer += decoder.decode(value, { stream: true });
    let nlIdx: number;
    while ((nlIdx = buffer.indexOf("\n")) !== -1) {
      let line = buffer.slice(0, nlIdx);
      buffer = buffer.slice(nlIdx + 1);
      if (line.endsWith("\r")) line = line.slice(0, -1);
      if (line.startsWith(":") || line.trim() === "") continue;
      if (!line.startsWith("data: ")) continue;
      const jsonStr = line.slice(6).trim();
      if (jsonStr === "[DONE]") {
        done = true;
        break;
      }
      try {
        const parsed = JSON.parse(jsonStr);
        const content = parsed.choices?.[0]?.delta?.content;
        if (content) onDelta(content);
      } catch {
        buffer = line + "\n" + buffer;
        break;
      }
    }
  }
  if (buffer.trim()) {
    for (const raw of buffer.split("\n")) {
      if (!raw || !raw.startsWith("data: ")) continue;
      const jsonStr = raw.slice(6).trim();
      if (jsonStr === "[DONE]") continue;
      try {
        const parsed = JSON.parse(jsonStr);
        const c = parsed.choices?.[0]?.delta?.content;
        if (c) onDelta(c);
      } catch {
        /* ignore */
      }
    }
  }
  onDone();
}

/** Chamada com documento anexado: retorna JSON { content, functionCall? } (suggest_archiving). */
async function institutionChatWithDocument({
  institutionId,
  messages,
  document,
  nivel,
  systemPromptExtra,
}: {
  institutionId: string;
  messages: Msg[];
  document: { base64: string; mimeType: string; filename?: string };
  nivel?: "A" | "B" | "C" | null;
  systemPromptExtra?: string | null;
}): Promise<{ content: string; functionCall?: { name: string; args: Record<string, unknown> } }> {
  const url = getSupabaseFunctionUrl("institution-ai");
  if (!url) throw new Error("Assistente temporariamente indisponível.");
  const token = await getAccessTokenForEdge();
  if (!token) throw new Error("Sessão expirada ou não autenticado. Faça login novamente.");
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({
      institution_id: institutionId,
      messages,
      document,
      nivel: nivel ?? null,
      system_prompt_extra: systemPromptExtra?.trim() || null,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const msg = (err as { error?: string })?.error;
    if (res.status === 502) {
      throw new Error(msg || "Serviço do assistente temporariamente indisponível (502). Aguarde alguns segundos e tente novamente.");
    }
    if (res.status === 503) {
      throw new Error(msg || "Assistente temporariamente indisponível. Tente novamente em instantes.");
    }
    if (res.status === 504) {
      throw new Error(msg || "A resposta demorou demais. Tente encurtar a pergunta ou enviar de novo.");
    }
    if (res.status === 403) {
      throw new Error(msg || "Sem permissão para usar o assistente nesta instituição.");
    }
    throw new Error(msg || `Erro ${res.status}`);
  }
  return res.json();
}

/** Normaliza e corrige erros comuns de transcrição de voz */
function normalizeAndCorrectTranscribedText(text: string): string {
  const corrections: [RegExp, string][] = [
    [/\bsindi\s*repa\b/gi, "Sindirepa"],
    [/\bsindirepa\b/gi, "Sindirepa"],
    [/\bcom\s*venção\b/gi, "convenção"],
    [/\bconvenção\s*coletiva\b/gi, "convenção coletiva"],
  ];
  let t = text.replace(/\s+/g, " ").trim();
  for (const [from, to] of corrections) {
    t = t.replace(from, to);
  }
  return t ? t.replace(/^./, (c) => c.toUpperCase()) : t;
}

const CLEO_VOICE_STORAGE_KEY = "cleo_voice_uri";
const fraseTesteVoz = (nome: string) =>
  `Olá, sou ${nome}, assistente desta instituição. Esta é a voz que você escolheu. Use o botão Ouvir para ouvir as respostas com ela.`;

/** Rate de fala da CLEO: 1.2 = 20% mais rápido para leitura fluida. */
const CLEO_SPEECH_RATE = 1.2;

/** Indica se a voz é feminina em pt-BR — usada para filtrar e priorizar. */
function isFemalePtBrVoice(voice: SpeechSynthesisVoice): boolean {
  const name = voice.name.toLowerCase();
  const lang = voice.lang.toLowerCase();
  if (!lang.startsWith("pt-br")) return false;
  return (
    name.includes("female") ||
    name.includes("feminin") ||
    name.includes("maria") ||
    name.includes("lúcia") ||
    name.includes("lucia") ||
    name.includes("luciana") ||
    name.includes("francisca") ||
    name.includes("daniela") ||
    name.includes("vitória") ||
    name.includes("vitoria") ||
    name.includes("amanda") ||
    name.includes("bruna") ||
    name.includes("camila") ||
    name.includes("fernanda") ||
    name.includes("gabriela") ||
    name.includes("julia") ||
    name.includes("larissa") ||
    name.includes("leticia") ||
    name.includes("renata") ||
    name.includes("mulher") ||
    name.includes("feminina")
  );
}

/** Prioridade para vozes pt-BR: Maria, Francisca/Neural, Google Português do Brasil primeiro. */
function voiceSortScore(voice: SpeechSynthesisVoice): number {
  const n = voice.name.toLowerCase();
  if (n.includes("maria")) return 100;
  if (n.includes("francisca")) return 98;
  if (n.includes("neural") && n.includes("pt")) return 92;
  if (n.includes("google") && (n.includes("português") || n.includes("brasil"))) return 95;
  if (n.includes("luciana") || n.includes("vitória") || n.includes("vitoria")) return 85;
  return 50;
}

/** Extrai frases completas do início do texto (terminadas em . ! ?) para TTS. Retorna { spoken: string, rest: string }. */
function takeCompleteSentences(text: string, maxChunk = 200): { spoken: string; rest: string } {
  const match = text.match(/^[\s\S]*?[.!?]\s+/);
  if (match) {
    const spoken = match[0].trim();
    return { spoken, rest: text.slice(spoken.length).trimStart() };
  }
  if (text.length >= maxChunk) return { spoken: text.slice(0, maxChunk).trim(), rest: text.slice(maxChunk).trimStart() };
  return { spoken: "", rest: text };
}

/** Remove markdown para TTS: blocos de código, headers, bold, etc. */
function stripMarkdownForTTS(markdown: string): string {
  const t = markdown
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`[^`]+`/g, " ")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/^#+\s+/gm, "")
    .replace(/\|[\s\-:|]+\|/g, " ")
    .replace(/\|[^|]+\|/g, (row) => row.replace(/\|/g, " ").trim())
    .replace(/\s+/g, " ")
    .trim();
  return t.slice(0, 3000); // limite razoável para áudio
}

export const CHAT_SESSION_ID = (instId: string, origin: "interno" | "externo", suffix?: string | null) => {
  const base = origin === "externo" ? `ext-inst-${instId}` : `inst-interno-${instId}`;
  const s = suffix?.trim().replace(/[^a-zA-Z0-9_-]/g, "-").slice(0, 80);
  return s ? `${base}--${s}` : base;
};

const ChatIAInstituicao = ({
  institutionId,
  institutionName,
  iaName: iaNameProp,
  nivel,
  chatSessionSuffix,
  systemPromptExtra,
  compact,
}: Props) => {
  /** Quando definido, o chat roda em portal (externo ou instituição): arquivamento vai para revisão do escritório. */
  const arquivamentoEnviarRevisao = nivel !== undefined;
  const nomeAssistente = iaNameProp?.trim() || "Assistente IA";
  const chatOrigin = nivel !== undefined ? "externo" : "interno";
  const sessionId = CHAT_SESSION_ID(institutionId, chatOrigin, chatSessionSuffix);
  const { toast } = useToast();
  const [messages, setMessages] = usePersistedChat<Msg>(sessionId, []);
  const prevSessionRef = useRef(sessionId);

  useEffect(() => {
    const prev = prevSessionRef.current;
    if (prev !== sessionId && prev) {
      clearPersistedChat(prev);
    }
    prevSessionRef.current = sessionId;
    if (typeof window === "undefined") return;
    try {
      const raw = localStorage.getItem(getStorageKey(sessionId));
      const parsed = raw ? (JSON.parse(raw) as Msg[]) : [];
      setMessages(Array.isArray(parsed) ? parsed : []);
    } catch {
      setMessages([]);
    }
  }, [sessionId, setMessages]);

  useEffect(() => {
    return () => {
      clearPersistedChat(sessionId);
    };
  }, [sessionId]);

  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [speakingMsgId, setSpeakingMsgId] = useState<string | null>(null);
  /** Quando o usuário clicou em "Ouvir" durante o streaming: voz acompanha o texto na tela. */
  const [speakingWhileStreamingId, setSpeakingWhileStreamingId] = useState<string | null>(null);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoiceUri, setSelectedVoiceUri] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(CLEO_VOICE_STORAGE_KEY);
  });
  const scrollRef = useRef<HTMLDivElement>(null);
  /** Posição de scroll a restaurar após TTS iniciar (evita "tela retorna" ao clicar Ouvir). */
  const scrollPositionToRestoreRef = useRef<{ viewportScrollTop: number; mainScrollTop?: number } | null>(null);
  const msgCounter = useRef(0);
  const genMsgId = () => `inst-${Date.now()}-${++msgCounter.current}`;
  const speakingWhileStreamingIdRef = useRef<string | null>(null);
  const streamingSpokenPlainLengthRef = useRef(0);
  const streamingTtsBufferRef = useRef("");
  const streamingContentRef = useRef("");
  const streamTtsFlushRef = useRef<((msgId: string, content: string) => void) | null>(null);
  /** Assets gerados pela IA (ilustrações, apresentações) por mensagem */
  const [generatedAssets, setGeneratedAssets] = useState<Record<string, { imageUrl?: string; gammaUrl?: string; type: "image" | "presentation"; loading?: boolean }>>({});
  /** Anexo para envio (análise + possível sugestão de arquivamento) */
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  /** Sugestões de arquivamento por id da mensagem da assistente */
  const [archivingSuggestions, setArchivingSuggestions] = useState<Record<string, SuggestArchivingMeta>>({});
  /** Arquivo associado a cada sugestão (para upload ao confirmar) */
  const [fileForArchiving, setFileForArchiving] = useState<Record<string, File>>({});
  /** Modal de revisão e arquivamento */
  const [archivingModal, setArchivingModal] = useState<{ open: boolean; msgId: string | null; meta: SuggestArchivingMeta | null; file: File | null }>({ open: false, msgId: null, meta: null, file: null });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const getPreferredVoice = useCallback((): SpeechSynthesisVoice | null => {
    const list = typeof window !== "undefined" ? window.speechSynthesis.getVoices() : [];
    const ptBrFemale = list.filter((v) => v.lang.toLowerCase().startsWith("pt-br") && isFemalePtBrVoice(v));
    if (selectedVoiceUri) {
      const found = ptBrFemale.find((v) => v.voiceURI === selectedVoiceUri);
      if (found) return found;
    }
    return ptBrFemale[0] ?? null;
  }, [selectedVoiceUri]);

  const handleSpeak = useCallback((msgId: string, content: string) => {
    if (typeof window === "undefined" || !window.speechSynthesis) {
      toast({ title: "Áudio", description: "Síntese de fala não disponível neste navegador.", variant: "destructive" });
      return;
    }
    const plain = stripMarkdownForTTS(content);
    if (!plain.trim()) {
      toast({ title: "Áudio", description: "Nada para ler.", variant: "destructive" });
      return;
    }
    // Preservar scroll antes do TTS para evitar "tela retorna" ao iniciar a voz
    const viewport = scrollRef.current?.firstElementChild as HTMLElement | null;
    const main = scrollRef.current?.closest("main") as HTMLElement | null;
    if (viewport?.scrollTop !== undefined) {
      scrollPositionToRestoreRef.current = {
        viewportScrollTop: viewport.scrollTop,
        mainScrollTop: main?.scrollTop,
      };
    }
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(plain);
    u.lang = "pt-BR";
    u.rate = CLEO_SPEECH_RATE;
    u.pitch = 1;
    const voice = getPreferredVoice();
    if (voice) u.voice = voice;
    u.onend = () => setSpeakingMsgId(null);
    u.onerror = () => setSpeakingMsgId(null);
    setSpeakingMsgId(msgId);
    window.speechSynthesis.speak(u);
  }, [toast, getPreferredVoice]);

  const handleStopSpeak = useCallback(() => {
    if (typeof window !== "undefined" && window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setSpeakingMsgId(null);
      setSpeakingWhileStreamingId(null);
      speakingWhileStreamingIdRef.current = null;
      streamingTtsBufferRef.current = "";
      streamingSpokenPlainLengthRef.current = 0;
    }
  }, []);

  /** Fala um trecho com a voz configurada (usado no modo "falar durante streaming"). */
  const speakChunk = useCallback(
    (plainText: string) => {
      if (typeof window === "undefined" || !window.speechSynthesis || !plainText.trim()) return;
      const u = new SpeechSynthesisUtterance(plainText);
      u.lang = "pt-BR";
      u.rate = CLEO_SPEECH_RATE;
      u.pitch = 1;
      const voice = getPreferredVoice();
      if (voice) u.voice = voice;
      u.onend = () => {
        if (speakingWhileStreamingIdRef.current) return;
        setSpeakingMsgId(null);
      };
      u.onerror = () => setSpeakingMsgId(null);
      window.speechSynthesis.speak(u);
    },
    [getPreferredVoice]
  );

  /** Enfileira e fala trechos do buffer (frases completas) durante o streaming. */
  const flushStreamingTts = useCallback(
    (msgId: string, content: string) => {
      if (speakingWhileStreamingIdRef.current !== msgId || typeof window === "undefined" || !window.speechSynthesis)
        return;
      const fullPlain = stripMarkdownForTTS(content);
      const delta = fullPlain.slice(streamingSpokenPlainLengthRef.current);
      streamingTtsBufferRef.current += delta;
      let buf = streamingTtsBufferRef.current;
      while (buf.length > 0) {
        const { spoken, rest } = takeCompleteSentences(buf, 180);
        if (spoken) {
          speakChunk(spoken);
          streamingSpokenPlainLengthRef.current += spoken.length;
          buf = rest;
        } else if (buf.length > 180) {
          speakChunk(buf.slice(0, 180));
          streamingSpokenPlainLengthRef.current += 180;
          buf = buf.slice(180);
        } else break;
      }
      streamingTtsBufferRef.current = buf;
    },
    [speakChunk]
  );

  useEffect(() => {
    streamTtsFlushRef.current = flushStreamingTts;
    return () => {
      streamTtsFlushRef.current = null;
    };
  }, [flushStreamingTts]);

  /** Ativa "Ouvir" durante o streaming: a voz acompanha o texto que vai aparecendo na tela. */
  const startSpeakingWhileStreaming = useCallback(
    (msgId: string, content: string) => {
      // Preservar scroll antes do TTS para evitar "tela retorna" ao iniciar a voz
      const viewport = scrollRef.current?.firstElementChild as HTMLElement | null;
      const main = scrollRef.current?.closest("main") as HTMLElement | null;
      if (viewport?.scrollTop !== undefined) {
        scrollPositionToRestoreRef.current = {
          viewportScrollTop: viewport.scrollTop,
          mainScrollTop: main?.scrollTop,
        };
      }
      setSpeakingMsgId(msgId);
      setSpeakingWhileStreamingId(msgId);
      speakingWhileStreamingIdRef.current = msgId;
      streamingSpokenPlainLengthRef.current = 0;
      streamingTtsBufferRef.current = "";
      const initialPlain = stripMarkdownForTTS(content);
      if (!initialPlain.trim()) return;
      const re = /[.!?]\s+/g;
      let start = 0;
      let match;
      while ((match = re.exec(initialPlain)) !== null) {
        const sentence = initialPlain.slice(start, match.index + match[0].length).trim();
        if (sentence) speakChunk(sentence);
        start = match.index + match[0].length;
      }
      const rest = initialPlain.slice(start).trim();
      if (rest) speakChunk(rest);
      streamingSpokenPlainLengthRef.current = initialPlain.length;
    },
    [speakChunk]
  );

  const [voicesLoaded, setVoicesLoaded] = useState(false);

  const handleClearChat = useCallback(() => {
    handleStopSpeak();
    setMessages([]);
    setGeneratedAssets({});
    setAttachedFile(null);
    setArchivingSuggestions({});
    setFileForArchiving({});
    setArchivingModal({ open: false, msgId: null, meta: null, file: null });
    clearPersistedChat(sessionId);
    toast({ title: "Conversa limpa", description: `O histórico de ${nomeAssistente} foi apagado.` });
  }, [sessionId, toast, handleStopSpeak, nomeAssistente]);

  useEffect(() => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    const onVoices = () => {
      const list = window.speechSynthesis.getVoices();
      setVoicesLoaded(list.length > 0);
      const ptBrFemale = list.filter((v) => v.lang.toLowerCase().startsWith("pt-br") && isFemalePtBrVoice(v));
      setVoices(ptBrFemale);
      const savedUri = localStorage.getItem(CLEO_VOICE_STORAGE_KEY);
      const savedValid = savedUri && ptBrFemale.some((v) => v.voiceURI === savedUri);
      if (!savedValid && ptBrFemale.length > 0) {
        const defaultVoice = ptBrFemale[0];
        if (defaultVoice) {
          localStorage.setItem(CLEO_VOICE_STORAGE_KEY, defaultVoice.voiceURI);
          setSelectedVoiceUri(defaultVoice.voiceURI);
        }
      }
    };
    onVoices();
    window.speechSynthesis.onvoiceschanged = onVoices;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, []);

  /** Restaura posição de scroll após TTS iniciar (evita "tela retorna"). */
  useEffect(() => {
    const toRestore = scrollPositionToRestoreRef.current;
    if (!toRestore) return;
    scrollPositionToRestoreRef.current = null;
    requestAnimationFrame(() => {
      const viewport = scrollRef.current?.firstElementChild as HTMLElement | null;
      if (viewport && toRestore.viewportScrollTop > 0) {
        viewport.scrollTop = toRestore.viewportScrollTop;
      }
      if (toRestore.mainScrollTop != null && toRestore.mainScrollTop > 0) {
        const main = scrollRef.current?.closest("main") as HTMLElement | null;
        if (main) main.scrollTop = toRestore.mainScrollTop;
      }
    });
  }, [speakingMsgId, speakingWhileStreamingId]);

  const testVoice = useCallback((voice: SpeechSynthesisVoice) => {
    if (typeof window === "undefined" || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(fraseTesteVoz(nomeAssistente));
    u.lang = "pt-BR";
    u.rate = CLEO_SPEECH_RATE;
    u.pitch = 1;
    u.voice = voice;
    window.speechSynthesis.speak(u);
  }, [nomeAssistente]);

  const selectVoice = useCallback((voice: SpeechSynthesisVoice) => {
    setSelectedVoiceUri(voice.voiceURI);
    localStorage.setItem(CLEO_VOICE_STORAGE_KEY, voice.voiceURI);
    toast({ title: `Voz de ${nomeAssistente}`, description: `Voz "${voice.name}" definida. Use Ouvir nas respostas.` });
  }, [toast, nomeAssistente]);

  // Ordenar: Maria, Google Português, femininas pt-BR primeiro (voices já filtrado para femininas)
  const sortedVoices = [...voices].sort((a, b) => voiceSortScore(b) - voiceSortScore(a));

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    });
  }, []);

  /** Processa tags [GERAR_ILUSTRACAO:...] e [GERAR_APRESENTACAO:...] na resposta do assistente */
  const processVisualTags = useCallback(
    async (msgId: string, content: string) => {
      const matchIlust = content.match(/\[GERAR_ILUSTRACAO:\s*(.+?)\]\s*$/s);
      const matchApres = content.match(/\[GERAR_APRESENTACAO:\s*(.+?)\]\s*$/s);
      const match = matchIlust || matchApres;
      if (!match) return;
      const prompt = match[1].trim();
      const type = matchIlust ? "image" as const : "presentation" as const;
      const tag = matchIlust ? /\[GERAR_ILUSTRACAO:\s*.+?\]\s*$/s : /\[GERAR_APRESENTACAO:\s*.+?\]\s*$/s;
      const cleaned = content.replace(tag, "").trim();
      setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, content: cleaned } : m)));
      setGeneratedAssets((prev) => ({ ...prev, [msgId]: { type, loading: true } }));
      try {
        const url = getSupabaseFunctionUrl("generate-image");
        if (!url) throw new Error("Serviço indisponível");
        const token = await getAccessTokenForEdge();
        if (!token) throw new Error("Sessão expirada. Faça login novamente.");
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ prompt, type: type === "presentation" ? "presentation" : undefined }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `Erro ${res.status}`);
        const gammaUrl = data.gammaUrl;
        const imageUrl = data.imageUrl;
        if (gammaUrl || imageUrl) {
          setGeneratedAssets((prev) => ({
            ...prev,
            [msgId]: { gammaUrl, imageUrl, type, loading: false },
          }));
          toast({
            title: type === "presentation" ? "Apresentação criada no Gamma" : "Ilustração gerada",
            description: type === "presentation" ? "Abra o link para ver e exportar em PowerPoint." : "A ilustração foi adicionada à resposta.",
          });
        } else throw new Error("Resposta sem URL");
      } catch (e) {
        setGeneratedAssets((prev) => ({ ...prev, [msgId]: { type, loading: false } }));
        toast({
          title: type === "presentation" ? "Erro na apresentação" : "Erro na ilustração",
          description: e instanceof Error ? e.message : "Erro ao gerar",
          variant: "destructive",
        });
      }
    },
    [toast]
  );

  const sendMessage = async () => {
    const text = input.trim();
    if ((!text && !attachedFile) || isLoading) return;

    const userMsg: Msg = {
      role: "user",
      content: text + (attachedFile ? `\n📄 ${attachedFile.name}` : ""),
      id: genMsgId(),
    };
    const fileToSend = attachedFile;
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setAttachedFile(null);
    setIsLoading(true);
    scrollToBottom();

    if (fileToSend) {
      const assistantId = genMsgId();
      try {
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            const dataUrl = reader.result as string;
            const b64 = dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
            resolve(b64 || "");
          };
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(fileToSend);
        });
        const mimeType = fileToSend.type || "application/octet-stream";
        const { content, functionCall } = await institutionChatWithDocument({
          institutionId,
          messages: [...messages, userMsg],
          document: { base64, mimeType, filename: fileToSend.name },
          nivel,
          systemPromptExtra,
        });
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: content || "Analisei o documento.", id: assistantId },
        ]);
        if (functionCall?.name === "suggest_archiving" && functionCall.args) {
          setArchivingSuggestions((prev) => ({
            ...prev,
            [assistantId]: functionCall.args as SuggestArchivingMeta,
          }));
          setFileForArchiving((prev) => ({ ...prev, [assistantId]: fileToSend }));
        }
        if (content) processVisualTags(assistantId, content);
      } catch (e) {
        const raw = e instanceof Error ? e.message : "Erro desconhecido";
        const rede =
          /failed to fetch|networkerror|load failed|name not resolved|internet disconnected/i.test(raw);
        toast({
          title: "Erro ao analisar documento",
          description: rede
            ? "Não foi possível contatar o servidor (rede ou DNS). Verifique a conexão e tente de novo."
            : raw,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
      return;
    }

    let assistantSoFar = "";
    const assistantId = genMsgId();
    const upsert = (chunk: string) => {
      assistantSoFar += chunk;
      streamingContentRef.current = assistantSoFar;
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "assistant" && last.id === assistantId) {
          return prev.map((m, i) =>
            i === prev.length - 1 ? { ...m, content: assistantSoFar } : m
          );
        }
        return [...prev, { role: "assistant", content: assistantSoFar, id: assistantId }];
      });
      scrollToBottom();
      streamTtsFlushRef.current?.(assistantId, assistantSoFar);
    };

    await streamInstitutionChat({
      institutionId,
      nivel,
      systemPromptExtra,
      messages: [...messages, userMsg],
      onDelta: upsert,
      onDone: () => {
        streamTtsFlushRef.current?.(assistantId, streamingContentRef.current);
        setSpeakingWhileStreamingId(null);
        speakingWhileStreamingIdRef.current = null;
        setIsLoading(false);
        const finalContent = streamingContentRef.current;
        if (finalContent) processVisualTags(assistantId, finalContent);
      },
      onError: (msg) => {
        const lower = msg.toLowerCase();
        const rede =
          lower.includes("failed to fetch") ||
          lower.includes("networkerror") ||
          lower.includes("load failed") ||
          /\berro\s+5\d\d\b/.test(lower) ||
          lower.includes("502") ||
          lower.includes("503") ||
          lower.includes("504");
        toast({
          title: "Erro",
          description: rede
            ? "Não foi possível concluir a resposta (rede ou servidor indisponível). Tente novamente em instantes."
            : msg,
          variant: "destructive",
        });
        setIsLoading(false);
      },
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Card
      className={
        compact
          ? "flex flex-col flex-1 min-h-[260px] max-h-[420px] border-0 shadow-none bg-transparent overflow-hidden"
          : "flex flex-col flex-1 min-h-[560px] h-[calc(100vh-280px)] max-h-[calc(100vh-180px)] border-0 shadow-none bg-transparent overflow-hidden"
      }
    >
      <ChatGradientWrapper areaOnly>
      <CardContent className="flex-1 flex flex-col p-0 overflow-hidden min-h-0">
        <div className="px-4 py-3 border-b border-amber-200/40 dark:border-amber-800/30 bg-gradient-to-r from-amber-100/60 to-slate-100/50 dark:from-amber-950/30 dark:to-slate-900/30">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 text-sm">
              <div className="flex items-center justify-center rounded-lg bg-amber-500/20 p-1.5">
                <Brain className="h-5 w-5 text-amber-700 dark:text-amber-400" />
              </div>
              <span className="font-semibold text-amber-900/90 dark:text-amber-100">
                {nomeAssistente} — Assistente de {institutionName}
              </span>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-muted-foreground hover:text-foreground gap-1"
                  onClick={handleClearChat}
                  title="Limpar conversa e apagar histórico"
                >
                  <Trash2 className="h-4 w-4" /> Limpar
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-muted-foreground hover:text-foreground gap-1"
                  onClick={async () => {
                    try {
                      await exportChatToPdfOfficial(messages, {
                        title: `Conversa com ${nomeAssistente} — Assistente IA`,
                        subtitle: `Instituição: ${institutionName}`,
                        assistantLabel: nomeAssistente,
                        filenamePrefix: `conversa-${nomeAssistente.replace(/\s+/g, "-")}-${institutionName}`,
                      });
                      toast({ title: "PDF exportado", description: "Conversa salva com sucesso." });
                    } catch (e) {
                      toast({ title: "Erro ao exportar", description: String(e), variant: "destructive" });
                    }
                  }}
                  title="Exportar conversa em PDF"
                >
                  <FileDown className="h-4 w-4" /> Exportar PDF
                </Button>
                </>
              )}
              <Popover>
                <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 text-muted-foreground hover:text-foreground" title={`Escolher voz de ${nomeAssistente}`}>
                  <Settings2 className="h-4 w-4" />
                </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80 p-0" align="end">
                <div className="p-2 border-b">
                  <p className="text-xs font-medium text-foreground">Voz de {nomeAssistente} (femininas, português brasileiro)</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Ouça cada voz e escolha a mais parecida. A preferência é salva.
                  </p>
                </div>
                <ScrollArea className="max-h-64 p-2">
                  {sortedVoices.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-2">
                      {!voicesLoaded ? "Carregando vozes..." : "Nenhuma voz feminina em português brasileiro disponível."}
                    </p>
                  ) : (
                    <div className="space-y-1">
                      {sortedVoices.map((voice) => (
                        <div key={voice.voiceURI} className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-muted/60">
                          <span className="text-xs truncate flex-1" title={voice.name}>
                            {voice.name}
                          </span>
                          <div className="flex gap-1 shrink-0">
                            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => testVoice(voice)}>Testar</Button>
                            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => selectVoice(voice)}>Usar</Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <p className="text-xs text-amber-800/80 dark:text-amber-200/80 mt-0.5">
            {nomeAssistente} responde com base nos documentos, usa tabelas, fluxogramas e gráficos, gera ilustrações e apresentações PowerPoint (Gamma) quando pedido, e fala em áudio.
          </p>
        </div>

        <div
          ref={scrollRef}
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-4 py-3"
        >
          {messages.length === 0 && (
            <div className="text-center text-muted-foreground text-sm py-4 sm:py-6 flex flex-col items-center">
              <div className="inline-flex items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500/25 to-slate-400/25 p-2.5 mb-2">
                <Brain className="h-9 w-9 text-amber-600 dark:text-amber-400" />
              </div>
              <p className="font-semibold text-foreground text-sm">
                Pergunte sobre os documentos desta instituição
              </p>
              <p className="text-xs mt-1 max-w-md mx-auto text-muted-foreground leading-snug">
                {nomeAssistente} consulta a base, cita fontes, usa tabelas, fluxogramas e gráficos. Peça ilustrações ou apresentações em PowerPoint quando precisar.
              </p>
              <div className="flex items-start gap-2 mt-3 px-3 py-2 rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-400 text-[11px] max-w-md mx-auto text-left leading-snug">
                <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-[1px]" />
                <span>Se a pergunta estiver fora da base, você será orientado a consultar o escritório.</span>
              </div>
              <div className="flex flex-wrap justify-center gap-1.5 mt-3 max-w-xl">
                {[
                  "O que diz a convenção sobre assembléias?",
                  "Resuma os principais pontos do último balanço",
                  "Quais são as normas de convocação?",
                ].map((q) => (
                  <Button
                    key={q}
                    variant="outline"
                    size="sm"
                    className="text-[11px] h-7 px-2.5 whitespace-normal text-left max-w-full"
                    onClick={() => setInput(q)}
                  >
                    {q}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-4">
            {messages.map((msg, i) => (
              <div key={msg.id || i}>
                <div
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div className={`max-w-[85%] ${msg.role === "user" ? "" : "flex flex-col items-start gap-1"}`}>
                    <div
                      className={`rounded-lg px-4 py-2.5 text-sm ${
                        msg.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-foreground"
                      }`}
                    >
                      {msg.role === "assistant" ? (
                        <LimiteMensagemChat>
                          <AiMarkdownRenderer content={msg.content} />
                        </LimiteMensagemChat>
                      ) : (
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      )}
                    </div>
                    {msg.role === "assistant" && (() => {
                      const id = msg.id ?? `idx-${i}`;
                      const asset = generatedAssets[id];
                      if (!asset) return null;
                      if (asset.loading)
                        return (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            {asset.type === "presentation" ? "Gerando apresentação no Gamma..." : "Gerando ilustração..."}
                          </div>
                        );
                      const url = asset.gammaUrl ?? asset.imageUrl;
                      if (!url) return null;
                      return (
                        <div className="mt-2 flex flex-col gap-1">
                          {asset.imageUrl && (
                            <a href={asset.gammaUrl || asset.imageUrl} target="_blank" rel="noopener noreferrer" className="block max-w-full">
                              <img src={asset.imageUrl} alt="Ilustração gerada" className="max-w-[280px] max-h-[200px] object-contain rounded border" />
                            </a>
                          )}
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                          >
                            {asset.type === "presentation" ? (
                              <><Presentation className="h-3 w-3" /> Abrir apresentação no Gamma (exportar PowerPoint)</>
                            ) : asset.imageUrl ? (
                              <><ExternalLink className="h-3 w-3" /> Ver ilustração</>
                            ) : (
                              <><ExternalLink className="h-3 w-3" /> Abrir ilustração no Gamma</>
                            )}
                          </a>
                        </div>
                      );
                    })()}
                    {msg.role === "assistant" && (() => {
                      const mid = msg.id ?? `idx-${i}`;
                      const suggestion = archivingSuggestions[mid];
                      if (!suggestion) return null;
                      return (
                        <Card key={`archiving-${mid}`} className="mt-2 w-full max-w-md border-amber-200 dark:border-amber-800 bg-amber-50/80 dark:bg-amber-950/30">
                          <CardContent className="p-3 text-left">
                            <p className="text-xs font-semibold text-amber-800 dark:text-amber-200 flex items-center gap-1">
                              <FileText className="h-3.5 w-3.5" /> Documento identificado
                            </p>
                            <p className="text-sm font-medium mt-1">{suggestion.title || "Sem título"}</p>
                            <p className="text-xs text-muted-foreground">
                              Categoria sugerida: {getDocumentTypeLabel(suggestion.document_type || "outro")}
                              {suggestion.legal_category ? ` · ${getLegalCategoryLabel(suggestion.legal_category)}` : ""}
                            </p>
                            {suggestion.summary && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{suggestion.summary}</p>
                            )}
                            <Button
                              size="sm"
                              className="mt-2 w-full"
                              onClick={() => setArchivingModal({
                                open: true,
                                msgId: mid,
                                meta: suggestion,
                                file: fileForArchiving[mid] ?? null,
                              })}
                            >
                              Revisar e Arquivar Definitivamente
                            </Button>
                          </CardContent>
                        </Card>
                      );
                    })()}
                    {msg.role === "assistant" && msg.content.trim() && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-xs text-muted-foreground hover:text-foreground gap-1"
                        onClick={() => {
                          const id = msg.id ?? `idx-${i}`;
                          const isStreamingThis =
                            isLoading &&
                            messages[messages.length - 1]?.role === "assistant" &&
                            (messages[messages.length - 1]?.id ?? `idx-${messages.length - 1}`) === id;
                          if (speakingMsgId === id) {
                            handleStopSpeak();
                          } else if (isStreamingThis) {
                            startSpeakingWhileStreaming(id, msg.content);
                          } else {
                            handleSpeak(id, msg.content);
                          }
                        }}
                        title={
                          speakingMsgId === (msg.id ?? `idx-${i}`)
                            ? "Parar áudio"
                            : "Ouvir resposta (voz feminina); durante a resposta, a voz acompanha o texto na tela."
                        }
                      >
                        {speakingMsgId === (msg.id ?? `idx-${i}`) ? (
                          <>
                            <Square className="h-3 w-3 fill-current" /> Parar
                          </>
                        ) : (
                          <>
                            <Volume2 className="h-3 w-3" /> Ouvir
                          </>
                        )}
                      </Button>
                    )}
                    {msg.role === "assistant" && msg.content.trim() && (
                      <AiMessageActions
                        messageId={msg.id || `inst-${i}`}
                        aiResponse={msg.content}
                        userMessage={messages[i - 1]?.role === "user" ? messages[i - 1].content : ""}
                        source="institution"
                        isLoading={isLoading}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
            {isLoading && messages[messages.length - 1]?.role !== "assistant" && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-lg px-4 py-2.5 text-sm flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" /> {nomeAssistente} consultando base...
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="border-t px-4 py-3 flex flex-col gap-2">
            {attachedFile && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="h-4 w-4" />
                <span className="truncate flex-1">{attachedFile.name}</span>
                <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setAttachedFile(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
            <div className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setAttachedFile(f);
                e.target.value = "";
              }}
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="shrink-0"
              title="Anexar documento (PDF ou imagem) para análise e possível arquivamento"
              onClick={() => fileInputRef.current?.click()}
            >
              <FileText className="h-4 w-4" />
            </Button>
            <VoiceCommandButton
              onTranscript={(text) => {
                const corrected = normalizeAndCorrectTranscribedText(text);
                setInput((prev) => (prev ? prev + " " + corrected : corrected).trim());
              }}
              disabled={isLoading}
            />
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Pergunte sobre os documentos desta instituição... Ou use o microfone para falar."
              className="min-h-[44px] max-h-[120px] resize-none flex-1"
              rows={1}
            />
            <Button onClick={sendMessage} disabled={isLoading || (!input.trim() && !attachedFile)} size="icon" className="shrink-0">
              <Send className="h-4 w-4" />
            </Button>
            </div>
        </div>

        <Dialog open={archivingModal.open} onOpenChange={(open) => !open && setArchivingModal({ open: false, msgId: null, meta: null, file: null })}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Revisar e Arquivar Documento</DialogTitle>
              <DialogDescription>
                {arquivamentoEnviarRevisao
                  ? "Após confirmar, o documento entra na fila de análise do escritório (Meus Pedidos e revisão interna)."
                  : "Confira título, tipo e pasta antes de gravar na base de conhecimento da instituição."}
              </DialogDescription>
            </DialogHeader>
            <ArchivingModalForm
              institutionId={institutionId}
              initialMeta={archivingModal.meta}
              file={archivingModal.file}
              enviarParaRevisaoEscritorio={arquivamentoEnviarRevisao}
              onSuccess={() => {
                if (archivingModal.msgId) {
                  setArchivingSuggestions((prev) => {
                    const next = { ...prev };
                    delete next[archivingModal.msgId!];
                    return next;
                  });
                  setFileForArchiving((prev) => {
                    const next = { ...prev };
                    delete next[archivingModal.msgId!];
                    return next;
                  });
                }
                setArchivingModal({ open: false, msgId: null, meta: null, file: null });
                toast(
                  arquivamentoEnviarRevisao
                    ? {
                        title: "Enviado para análise do escritório",
                        description: "O documento aparece em Meus Pedidos e na fila de revisão até aprovação.",
                      }
                    : {
                        title: "Documento arquivado",
                        description: "O documento foi salvo na base da instituição.",
                      },
                );
              }}
              onCancel={() => setArchivingModal({ open: false, msgId: null, meta: null, file: null })}
              toast={toast}
            />
          </DialogContent>
        </Dialog>
      </CardContent>
      </ChatGradientWrapper>
    </Card>
  );
};

export default ChatIAInstituicao;
